import { ONE_MINUTE } from '@/constants/time';
import { getPlatformStorage } from '@/util/platformStorage';

/**
 * Persists support-account impersonation across page reloads with a sliding idle TTL.
 *
 * The stored entry is bound to the authenticated user that set it: hydration only
 * restores impersonation when the same user is logged in. Activity is recorded by
 * `touchImpersonation` (called from the API client middleware on every request that
 * carries an `X-Act-As` header) and the entry expires after `IMPERSONATION_IDLE_TIMEOUT_MS`
 * with no recorded activity.
 */

const STORAGE_KEY = 'primum_act_as_user_id';

export const IMPERSONATION_IDLE_TIMEOUT_MS = 30 * ONE_MINUTE;

const TOUCH_THROTTLE_MS = ONE_MINUTE;

interface StoredImpersonation {
  authenticatedUserId: string;
  actAsUserId: string;
  lastActivityAt: number;
}

const isStoredImpersonation = (value: unknown): value is StoredImpersonation =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as StoredImpersonation).authenticatedUserId === 'string' &&
  typeof (value as StoredImpersonation).actAsUserId === 'string' &&
  typeof (value as StoredImpersonation).lastActivityAt === 'number';

const storage = () => getPlatformStorage(undefined, 'local');

let lastTouchedAt = 0;

const isExpired = (lastActivityAt: number) =>
  Date.now() - lastActivityAt > IMPERSONATION_IDLE_TIMEOUT_MS;

export async function saveImpersonation(authenticatedUserId: string, actAsUserId: string) {
  const payload: StoredImpersonation = {
    authenticatedUserId,
    actAsUserId,
    lastActivityAt: Date.now(),
  };

  lastTouchedAt = payload.lastActivityAt;

  try {
    await storage().setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {}
}

export async function clearImpersonation() {
  lastTouchedAt = 0;

  try {
    await storage().removeItem(STORAGE_KEY);
  } catch {}
}

/**
 * Returns the persisted impersonated user id when the entry belongs to the supplied
 * authenticated user and has not exceeded the idle TTL. Stale or mismatched entries
 * are removed before returning `undefined`.
 */
export async function loadImpersonation(authenticatedUserId: string): Promise<string | undefined> {
  try {
    const raw = await storage().getItem(STORAGE_KEY);

    if (!raw) {
      return undefined;
    }

    const parsed: unknown = JSON.parse(raw);

    if (!isStoredImpersonation(parsed)) {
      await clearImpersonation();

      return undefined;
    }

    if (parsed.authenticatedUserId !== authenticatedUserId || isExpired(parsed.lastActivityAt)) {
      await clearImpersonation();

      return undefined;
    }

    lastTouchedAt = parsed.lastActivityAt;

    return parsed.actAsUserId;
  } catch {
    return undefined;
  }
}

/**
 * Records activity for the active impersonation. Throttled so it writes at most once
 * per `TOUCH_THROTTLE_MS`. Safe to call from the request middleware on every API call.
 *
 * Skips the write when the stored entry no longer matches the supplied identity — this
 * prevents an in-flight request whose middleware captured the previous impersonation
 * from clobbering a fresh `switchToAccount` write.
 */
export async function touchImpersonation(authenticatedUserId: string, actAsUserId: string) {
  const now = Date.now();

  if (now - lastTouchedAt < TOUCH_THROTTLE_MS) {
    return;
  }

  try {
    const raw = await storage().getItem(STORAGE_KEY);

    if (!raw) {
      return;
    }

    const parsed: unknown = JSON.parse(raw);

    if (
      !isStoredImpersonation(parsed) ||
      parsed.authenticatedUserId !== authenticatedUserId ||
      parsed.actAsUserId !== actAsUserId
    ) {
      return;
    }
  } catch {
    return;
  }

  await saveImpersonation(authenticatedUserId, actAsUserId);
}

/** Test-only: reset the in-memory throttle so tests aren't coupled by ordering. */
export function __resetImpersonationThrottleForTests() {
  lastTouchedAt = 0;
}
